<template>
  <Card :link-path="cardLinkPath" :link-text="cardLinkText" :title="t('global.contacts')">
    <DataTable
        :headers="contactHeader"
        :is-loading="isLoading"
        :items="tableContacts"
        @delete="deleteContact"
    />
  </Card>
</template>
<script lang="ts" setup>
import {computed, ref} from "vue";
import {useI18n} from "vue3-i18n";
import {useMemberStore} from "@/stores/memberStore";
import {Guid, Role} from "@/types";
import {Contact} from "@/types/entities/contact";
import DataTable from "@/components/layout/DataTable.vue";
import Card from "@/components/layout/Card.vue";
import {getAuthenticatedMemberOrganizationRoute} from "@/router/helpers";
import {useContactService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";

// eslint-disable-next-line
const props = defineProps<{
  contacts: Contact[]
  organizationId: string
  cardLinkPath: any
  isLoading: boolean
}>();

const {t} = useI18n()
const memberStore = useMemberStore()
const contactService = useContactService()

const contacts = ref<Contact[]>(props.contacts);

const canAddEditOrDeleteContact = memberStore.hasOneOfTheseRoles([Role.Admin, Role.CorporateAdmin])

const tableContacts = computed(() => {
  return contacts.value.map((x: any) => {
    if (canAddEditOrDeleteContact) {
      x.actions = {
        update: {
          name: memberStore.hasRole(Role.Admin)
            ? 'admin.children.organizations.editContact'
            : `${getAuthenticatedMemberOrganizationRoute()}.children.editContact`,
          params: {organizationId: props.organizationId, contactId: x.id}
        },
        delete: true
      }
    }
    return x;
  }) as Contact[];
})

async function deleteContact(contact: any) {
  if (contact?.id == null || !Guid.isValid(contact.id ?? ""))
    return;

  let confirmDelete = confirm(t("validation.contact.delete.confirmation"));
  if (!confirmDelete)
    return;

  let response = await contactService.deleteContact(contact.id, props.organizationId)
  if (response.succeeded) {
    contacts.value = contacts.value.filter(x => x.id !== contact.id)
    notifySuccess(t('validation.contact.delete.success'))
  } else {
    let errorMessages = response.getErrorMessages('validation.contact.delete',
      'validation.member.contact.errorOccured')
    notifyError(errorMessages[0])
  }
}

const cardLinkPath = canAddEditOrDeleteContact ? props.cardLinkPath : undefined;
const cardLinkText = canAddEditOrDeleteContact ? t('organization.addContact') : undefined;

const contactHeader = [
  {
    text: t("global.firstName"),
    value: "firstName",
    sortable: true,
    width: 125,
  },
  {
    text: t("global.lastName"),
    value: "lastName",
    sortable: true,
    width: 125,
  },
  {
    text: t("global.email"),
    value: "email",
    sortable: true,
    width: 125,
  }
];

if (canAddEditOrDeleteContact) {
  contactHeader.push({
    text: t("global.table.actions"),
    value: "actions",
    sortable: false,
    width: 75
  })
}

</script>